// Operator console sign-in. An internal security-ops console (not a marketing
// site): a full-bleed ambient hero (animated brand aurora + faint grid, fully
// reduced-motion aware) with a floating glass sign-in card. Honest auth — no
// public signup; access is provisioned by an administrator. Real, live service
// status via the unauthenticated /health endpoint.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLogto } from '@logto/react';
import { useAuth, type VerifyStatus } from '@/auth/AuthContext';
import { useUIStore } from '@/stores/ui.store';
import { isOidcFlow, SIGN_IN_REDIRECT_URI } from '@/auth/logto-config';
import { IOSentraLogo } from '@/components/IOSentraLogo';
import { IOSentraWordmark } from '@/components/IOSentraWordmark';
import { VerificationStep } from '@/pages/VerificationStep';

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.1';
const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL || 'support@iosentra.io';
const API_BASE = import.meta.env.VITE_API_URL || '';

// Tagline keys — the strings live in auth.json (auth:taglines.*) so they
// translate + rotate by index.
const TAGLINE_KEYS = ['taglines.line1', 'taglines.line2', 'taglines.line3', 'taglines.line4'];

type ServiceState = 'up' | 'down' | 'checking';

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { logtoLogin, logtoSendCode, logtoVerify, logtoEnrollTotp, logtoBindTotp, requestPasswordReset, confirmPasswordReset } = useAuth();
  const { signIn } = useLogto();
  const navigate = useNavigate();
  // Language: reuse the global store setter (persists to localStorage, flips
  // RTL/dir, switches i18n, best-effort API sync). Works pre-auth.
  const language = useUIStore((s) => s.language);
  const setLanguage = useUIStore((s) => s.setLanguage);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [service, setService] = useState<ServiceState>('checking');
  const [tagline, setTagline] = useState(0);
  // MFA step: after the password, the Logto flow may require a second factor.
  // loginId ties the interaction; method is the chosen factor (Email/Phone/Totp).
  const [loginId, setLoginId] = useState<string | null>(null);
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [mfaMethod, setMfaMethod] = useState<'Email' | 'Phone' | 'Totp'>('Totp');
  const [code, setCode] = useState('');
  // Authenticator-enrollment sub-step: for factorless users the backend returns
  // enrollMethods:['Totp'] (empty mfaMethods). We fetch a secret + QR and show a
  // "set up authenticator" card; confirming the first code binds it and finishes login.
  const [enroll, setEnroll] = useState<{ secret: string; qrCode: string } | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const challenge = loginId && !enroll; // reuse the existing 2FA-step markup condition (verify only)
  // Forgot-password sub-step: 'email' collects the address (→ emails a code),
  // 'reset' collects the code + new password. resetId ties the two calls.
  const [forgot, setForgot] = useState<null | { stage: 'email' | 'reset'; resetId?: string }>(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPw, setResetPw] = useState('');
  const [resetDone, setResetDone] = useState(false);
  // Stage-4 contact-verification step: after password/MFA, the account may still
  // need email/phone verified before a session is granted.
  const [verify, setVerify] = useState<{ token: string; status?: VerifyStatus } | null>(null);

  // Rotate the tagline every ~5s (paused under reduced-motion via CSS-only fade;
  // the swap itself is harmless and the line stays readable).
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = setInterval(() => setTagline((i) => (i + 1) % TAGLINE_KEYS.length), 5000);
    return () => clearInterval(id);
  }, []);

  // Real, unauthenticated service-status ping (no fake green dot).
  useEffect(() => {
    let alive = true;
    const ping = () => fetch(`${API_BASE}/health`, { cache: 'no-store' })
      .then((r) => { if (alive) setService(r.ok ? 'up' : 'down'); })
      .catch(() => { if (alive) setService('down'); });
    ping();
    const id = setInterval(ping, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await logtoLogin(email, password);
      if (r.verificationRequired && r.verifyToken) {
        setVerify({ token: r.verifyToken, status: r.verifyStatus });
      } else if (r.mfaRequired && r.loginId) {
        setLoginId(r.loginId); setCode('');
        const methods = r.mfaMethods ?? [];
        // No bound factor but the account requires MFA → enroll an authenticator.
        if (methods.length === 0 && (r.enrollMethods ?? []).includes('Totp')) {
          const en = await logtoEnrollTotp(r.loginId);
          setEnroll(en);
        } else {
          setMfaMethods(methods.length ? methods : ['Totp']);
          // Default to the first offered factor; prefer TOTP if present.
          const first = (methods.includes('Totp') ? 'Totp' : methods[0] ?? 'Totp') as 'Email' | 'Phone' | 'Totp';
          setMfaMethod(first);
          // For email/phone factors, send the code immediately.
          if (first === 'Email' || first === 'Phone') { try { await logtoSendCode(r.loginId, first); } catch { /* surfaced on verify */ } }
        }
      } else {
        // Session is set; leave /login so we don't land on the authed 404.
        navigate('/', { replace: true });
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('signInFailed');
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await logtoVerify(loginId!, mfaMethod, code.trim());
      if (r.verificationRequired && r.verifyToken) {
        setVerify({ token: r.verifyToken, status: r.verifyStatus });
      } else {
        navigate('/', { replace: true });
      }
    } catch {
      setError(t('twoFactor.codeFailed'));
    } finally {
      setBusy(false);
    }
  };

  // Confirm the first authenticator code → bind the factor and finish the login.
  const onEnrollConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await logtoBindTotp(loginId!, code.trim());
      if (r.verificationRequired && r.verifyToken) {
        setVerify({ token: r.verifyToken, status: r.verifyStatus });
      } else {
        navigate('/', { replace: true });
      }
    } catch {
      setError(t('twoFactor.codeFailed'));
    } finally {
      setBusy(false);
    }
  };

  // Forgot password — step 1: email a reset code. Anti-enumeration: we always
  // advance to the code step (a resetId is returned only when a code was sent,
  // but we never reveal whether the address exists).
  const onForgotRequest = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const { resetId } = await requestPasswordReset(resetEmail.trim());
      setForgot({ stage: 'reset', resetId });
      setResetCode(''); setResetPw('');
    } catch {
      setError(t('resetPassword.requestFailed', { defaultValue: "Couldn't start a reset. Try again." }));
    } finally { setBusy(false); }
  };
  // Forgot password — step 2: verify the code + set the new password.
  const onForgotConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      if (!forgot?.resetId) { setError(t('resetPassword.expired', { defaultValue: 'This reset expired — start again.' })); setForgot({ stage: 'email' }); return; }
      await confirmPasswordReset(forgot.resetId, resetCode.trim(), resetPw);
      setResetDone(true);
    } catch (err: unknown) {
      const code = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.data?.error;
      setError(
        code === 'invalid_code' ? t('resetPassword.codeFailed', { defaultValue: 'That code is incorrect or expired.' })
        : code === 'weak_password' ? t('resetPassword.weakPassword', { defaultValue: 'Choose a stronger password (min 8 characters).' })
        : code === 'reset_expired' ? t('resetPassword.expired', { defaultValue: 'This reset expired — start again.' })
        : t('resetPassword.requestFailed', { defaultValue: "Couldn't reset your password. Try again." }),
      );
    } finally { setBusy(false); }
  };
  const closeForgot = () => { setForgot(null); setResetEmail(''); setResetCode(''); setResetPw(''); setResetDone(false); setError(null); };

  // Switch factor (e.g. TOTP → email) and (re)send if it's an OTP method.
  const chooseMethod = async (m: 'Email' | 'Phone' | 'Totp') => {
    setMfaMethod(m); setCode(''); setError(null);
    if ((m === 'Email' || m === 'Phone') && loginId) {
      try { await logtoSendCode(loginId, m); } catch { setError(t('twoFactor.codeFailed')); }
    }
  };

  const capsCheck = (e: React.KeyboardEvent) => {
    const on = e.getModifierState?.('CapsLock');
    if (typeof on === 'boolean') setCapsOn(on);
  };

  return (
    <div className="flex min-h-full flex-col">

      <main className="relative grid flex-1 place-items-center overflow-hidden px-6 py-8">
        {/* Language selector — top corner (end-aligned, so it flips with RTL).
            Uses the global store: persists + flips dir + switches i18n. */}
        <div className="absolute top-5 end-5 z-10 flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-0.5 text-xs backdrop-blur">
          <Globe size={13} className="ms-1.5 text-muted" aria-hidden />
          {([['en', 'EN'], ['he', 'עברית']] as const).map(([code, label]) => (
            <button
              key={code}
              type="button"
              onClick={() => setLanguage(code)}
              aria-pressed={language === code}
              className={`rounded-full px-2.5 py-1 transition ${
                language === code ? 'bg-primary/20 text-fg' : 'text-muted hover:text-fg'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {/* Slow-drifting brand aurora — subtle life behind the card. Transform-only
            blobs (GPU-cheap); frozen under prefers-reduced-motion (index.css). */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="tk-aurora-a absolute -top-40 start-[8%] h-[34rem] w-[34rem] rounded-full bg-primary/[0.13] blur-3xl" />
          <div className="tk-aurora-b absolute -bottom-48 end-[6%] h-[30rem] w-[30rem] rounded-full bg-[#00D4C3]/[0.07] blur-3xl" />
          <div className="tk-aurora-a absolute top-1/3 end-[30%] h-72 w-72 rounded-full bg-primary/[0.08] blur-3xl" style={{ animationDelay: '-14s' }} />
        </div>
        <div className="relative w-full max-w-lg">
          {/* Brand lockup */}
          <div className="mb-10 flex items-center justify-center gap-3.5">
            <span className="grid h-16 w-16 place-items-center rounded-2xl bg-primary/10 ring-1 ring-primary/25 shadow-[0_0_40px_-6px_rgba(56,142,255,0.6)]">
              <IOSentraLogo size={40} />
            </span>
            <div className="leading-tight">
              <div className="text-3xl"><IOSentraWordmark /></div>
              <div className="text-sm text-muted/70">{t('brandTagline')}</div>
            </div>
          </div>

          {/* The glass sign-in card, floating over the aurora. */}
          <div className="tk-modal p-10">
            <h1 className="text-[1.65rem] font-semibold">{t('welcomeBack')}</h1>
            <p className="mt-1 text-[15px] text-muted">{t('subtitle')}</p>

            {isOidcFlow ? (
              /* ── Unified Logto sign-in (AUTH_FLOW=oidc) ── */
              /* The password form, social, SSO, MFA, and passkeys all live in the
                 IOSentra-themed Logto experience. This button redirects there; the
                 return trip lands on /callback (CallbackPage) which exchanges the
                 token for an IOSentra session. */
              <div className="mt-7 space-y-4">
                {error && (
                  <div className="flex items-center gap-2 rounded-md border border-critical/30 bg-critical/10 px-3 py-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-critical" />
                    <p className="text-sm text-critical">{error}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={async () => { try { await signIn(SIGN_IN_REDIRECT_URI); } catch { setError(t('signInFailed')); } }}
                  className="tk-btn-primary w-full py-3 text-base"
                >
                  {t('common:actions.signIn')}
                </button>
                <p className="text-center text-xs text-muted">{t('oidcHint', 'Continue with email, Microsoft, Google, SSO, or a passkey.')}</p>
              </div>
            ) : verify ? (
              /* ── Contact-verification step (Stage 4) ── */
              <VerificationStep
                verifyToken={verify.token}
                initialStatus={verify.status}
                onComplete={() => navigate('/', { replace: true })}
              />
            ) : forgot ? (
              /* ── Forgot-password sub-step (email → code + new password) ── */
              resetDone ? (
                <div className="mt-7 space-y-4">
                  <div className="rounded-md border border-ok/30 bg-ok/10 px-3 py-2.5">
                    <p className="text-sm text-ok">{t('resetPassword.done', { defaultValue: 'Your password has been reset. You can sign in now.' })}</p>
                  </div>
                  <button type="button" onClick={closeForgot} className="tk-btn-primary w-full py-2.5 text-base">{t('resetPassword.backToSignIn', { defaultValue: 'Back to sign in' })}</button>
                </div>
              ) : forgot.stage === 'email' ? (
                <form onSubmit={onForgotRequest} className="mt-7 space-y-4">
                  <div>
                    <h2 className="text-base font-semibold text-fg">{t('resetPassword.title', { defaultValue: 'Reset your password' })}</h2>
                    <p className="mt-1 text-xs text-muted">{t('resetPassword.emailIntro', { defaultValue: "Enter your email and we'll send you a code to reset your password." })}</p>
                  </div>
                  <div>
                    <label htmlFor="reset-email" className="mb-1.5 block text-sm text-muted">{t('email')}</label>
                    <div className="relative">
                      <Mail size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                      <input id="reset-email" type="email" autoComplete="username" autoFocus required
                        value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                        className="tk-input py-3 pl-10 text-base" placeholder={t('emailPlaceholder')} />
                    </div>
                  </div>
                  {error && (
                    <div className="flex items-center gap-2 rounded-md border border-critical/30 bg-critical/10 px-3 py-2">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-critical" /><p className="text-sm text-critical">{error}</p>
                    </div>
                  )}
                  <button type="submit" className="tk-btn-primary w-full py-2.5 text-base" disabled={busy || !resetEmail}>
                    {busy ? t('resetPassword.sending', { defaultValue: 'Sending…' }) : t('resetPassword.sendCode', { defaultValue: 'Send reset code' })}
                  </button>
                  <button type="button" onClick={closeForgot} className="w-full text-center text-xs text-muted hover:text-fg">{t('twoFactor.backToSignIn')}</button>
                </form>
              ) : (
                <form onSubmit={onForgotConfirm} className="mt-7 space-y-4">
                  <div>
                    <h2 className="text-base font-semibold text-fg">{t('resetPassword.title', { defaultValue: 'Reset your password' })}</h2>
                    <p className="mt-1 text-xs text-muted">{t('resetPassword.codeIntro', { defaultValue: 'Enter the 6-digit code we emailed you and choose a new password.' })}</p>
                  </div>
                  <div>
                    <label htmlFor="reset-code" className="mb-1.5 block text-sm text-muted">{t('twoFactor.code')}</label>
                    <input id="reset-code" inputMode="numeric" autoComplete="one-time-code" autoFocus required
                      value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="tk-input py-3 text-center text-lg tracking-[0.4em]" placeholder="000000" />
                  </div>
                  <div>
                    <label htmlFor="reset-pw" className="mb-1.5 block text-sm text-muted">{t('resetPassword.newPassword', { defaultValue: 'New password' })}</label>
                    <div className="relative">
                      <Lock size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                      <input id="reset-pw" type={showPw ? 'text' : 'password'} autoComplete="new-password" required minLength={8}
                        value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                        className="tk-input py-3 px-10 text-base" placeholder="••••••••" />
                      <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? t('hidePassword') : t('showPassword')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-fg">
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  {error && (
                    <div className="flex items-center gap-2 rounded-md border border-critical/30 bg-critical/10 px-3 py-2">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-critical" /><p className="text-sm text-critical">{error}</p>
                    </div>
                  )}
                  <button type="submit" className="tk-btn-primary w-full py-2.5 text-base" disabled={busy || resetCode.length !== 6 || resetPw.length < 8}>
                    {busy ? t('resetPassword.resetting', { defaultValue: 'Resetting…' }) : t('resetPassword.reset', { defaultValue: 'Reset password' })}
                  </button>
                  <button type="button" onClick={() => setForgot({ stage: 'email' })} className="block w-full text-center text-xs text-primary hover:opacity-80">{t('resetPassword.resend', { defaultValue: "Didn't get a code? Send again" })}</button>
                  <button type="button" onClick={closeForgot} className="w-full text-center text-xs text-muted hover:text-fg">{t('twoFactor.backToSignIn')}</button>
                </form>
              )
            ) : enroll ? (
              /* ── Authenticator-enrollment step (factorless user) ── */
              <form onSubmit={onEnrollConfirm} className="mt-7 space-y-4">
                <div>
                  <h2 className="text-base font-semibold text-fg">{t('totpSetup.title', { defaultValue: 'Set up your authenticator' })}</h2>
                  <p className="mt-1 text-xs text-muted">{t('totpSetup.intro', { defaultValue: 'Scan this QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code it shows.' })}</p>
                </div>
                <div className="flex justify-center">
                  <img src={enroll.qrCode} alt={t('totpSetup.qrAlt', { defaultValue: 'Authenticator QR code' })} width={168} height={168} className="rounded-lg bg-white p-2" />
                </div>
                <div>
                  <p className="mb-1 text-xs text-muted">{t('totpSetup.manual', { defaultValue: "Can't scan? Enter this key manually:" })}</p>
                  <button
                    type="button"
                    onClick={() => { void navigator.clipboard?.writeText(enroll.secret).then(() => { setSecretCopied(true); setTimeout(() => setSecretCopied(false), 1500); }); }}
                    className="w-full break-all rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-center font-mono text-sm tracking-wider text-fg hover:border-primary/40"
                    title={t('totpSetup.copy', { defaultValue: 'Copy to clipboard' })}
                  >
                    {enroll.secret}
                    <span className="ms-2 text-[10px] uppercase text-primary">{secretCopied ? t('totpSetup.copied', { defaultValue: 'Copied' }) : t('totpSetup.copy', { defaultValue: 'Copy' })}</span>
                  </button>
                </div>
                <div>
                  <label htmlFor="enroll-code" className="mb-1.5 block text-sm text-muted">{t('twoFactor.code')}</label>
                  <input
                    id="enroll-code" inputMode="numeric" autoComplete="one-time-code" autoFocus required
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="tk-input py-3 text-center text-lg tracking-[0.4em]" placeholder="000000"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 rounded-md border border-critical/30 bg-critical/10 px-3 py-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-critical" />
                    <p className="text-sm text-critical">{error}</p>
                  </div>
                )}
                <button type="submit" className="tk-btn-primary w-full py-2.5 text-base" disabled={busy || code.length !== 6}>
                  {busy ? t('twoFactor.verifying') : t('totpSetup.confirm', { defaultValue: 'Verify & continue' })}
                </button>
                <button type="button" onClick={() => { setEnroll(null); setLoginId(null); setCode(''); setError(null); }} className="w-full text-center text-xs text-muted hover:text-fg">{t('twoFactor.backToSignIn')}</button>
              </form>
            ) : challenge ? (
              /* ── 2FA step ── */
              <form onSubmit={onVerify} className="mt-7 space-y-4">
                {/* One method at a time (auto-selected). No up-front chooser. */}
                <div>
                  <label htmlFor="totp" className="mb-1.5 block text-sm text-muted">{t('twoFactor.code')}</label>
                  <p className="mb-2 text-xs text-muted">
                    {mfaMethod === 'Totp' ? t('twoFactor.enterTotp', { defaultValue: 'Enter the 6-digit code from your authenticator app.' })
                      : mfaMethod === 'Email' ? t('twoFactor.sentEmail', { defaultValue: 'We emailed you a 6-digit code. Enter it below.' })
                      : t('twoFactor.sentSms', { defaultValue: 'We texted you a 6-digit code. Enter it below.' })}
                  </p>
                  <input
                    id="totp" inputMode="numeric" autoComplete="one-time-code" autoFocus required
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="tk-input py-3 text-center text-lg tracking-[0.4em]" placeholder="000000"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 rounded-md border border-critical/30 bg-critical/10 px-3 py-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-critical" />
                    <p className="text-sm text-critical">{error}</p>
                  </div>
                )}
                <button type="submit" className="tk-btn-primary w-full py-2.5 text-base" disabled={busy || code.length !== 6}>
                  {busy ? t('twoFactor.verifying') : t('twoFactor.verify')}
                </button>
                {/* Subtle secondary actions BELOW the button. */}
                {mfaMethod !== 'Totp' && (
                  <button type="button" onClick={() => chooseMethod(mfaMethod)} className="block w-full text-center text-xs text-primary hover:opacity-80">
                    {t('twoFactor.resend', { defaultValue: 'Resend code' })}
                  </button>
                )}
                {mfaMethods.length > 1 && (() => {
                  const other = mfaMethods.find((m) => m !== mfaMethod) as 'Email' | 'Phone' | 'Totp' | undefined;
                  if (!other) return null;
                  return (
                    <button type="button" onClick={() => chooseMethod(other)} className="block w-full text-center text-xs text-primary hover:opacity-80">
                      {other === 'Totp' ? t('twoFactor.useAuthenticator', { defaultValue: 'Use authenticator app instead' })
                        : other === 'Email' ? t('twoFactor.useEmail', { defaultValue: 'Email me a code instead' })
                        : t('twoFactor.useSms', { defaultValue: 'Text me a code instead' })}
                    </button>
                  );
                })()}
                <button type="button" onClick={() => { setLoginId(null); setMfaMethods([]); setCode(''); setError(null); }} className="w-full text-center text-xs text-muted hover:text-fg">{t('twoFactor.backToSignIn')}</button>
              </form>
            ) : (
            <form onSubmit={onSubmit} className="mt-7 space-y-5">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-[15px] text-muted">{t('email')}</label>
                <div className="relative">
                  <Mail size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    id="email" type="email" autoComplete="username" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="tk-input py-3 pl-10 text-base" placeholder={t('emailPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="block text-[15px] text-muted">{t('password')}</label>
                  <button type="button" onClick={() => { setForgot({ stage: 'email' }); setResetEmail(email); setError(null); }} className="-my-1.5 -mx-2 inline-block px-2 py-1.5 text-xs text-muted hover:text-fg">{t('forgotPassword')}</button>
                </div>
                <div className="relative">
                  <Lock size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    id="password" type={showPw ? 'text' : 'password'} autoComplete="current-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    onKeyUp={capsCheck} onKeyDown={capsCheck}
                    className="tk-input py-3 px-10 text-base" placeholder="••••••••"
                  />
                  <button
                    type="button" onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? t('hidePassword') : t('showPassword')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-fg"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {capsOn && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-warning">
                    <span className="h-1.5 w-1.5 rounded-full bg-warning" /> {t('capsLockOn')}
                  </p>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-md border border-critical/30 bg-critical/10 px-3 py-2">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-critical" />
                  <p className="text-sm text-critical">{error}</p>
                </div>
              )}

              <button type="submit" className="tk-btn-primary w-full py-2.5 text-base" disabled={busy}>
                {busy ? (
                  <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> {t('signingIn')}</>
                ) : t('common:actions.signIn')}
              </button>
            </form>
            )}

          </div>

          {/* Quiet rotating product line — confident framing, not a brochure. */}
          <p key={tagline} className="tk-tagline mt-5 text-center text-sm text-muted/90">
            {t(TAGLINE_KEYS[tagline])}
          </p>

          {/* No public signup — access is provisioned by an administrator. */}
          <p className="mt-5 text-center text-xs text-muted">
            {t('needAccess')} <a href={`mailto:${SUPPORT_EMAIL}?subject=IOSentra%20access%20request`} className="text-primary hover:opacity-80">{t('contactAdmin')}</a>.
          </p>
        </div>
      </main>

      {/* Real footer — with a LIVE service-status dot from /health. */}
      <footer className="flex flex-col items-center justify-between gap-2 border-t border-white/5 px-6 py-4 text-xs text-muted sm:flex-row">
        <span className="flex items-center gap-1.5" title={`API: ${service}`}>
          {service === 'up' ? (
            <><span className="tk-pulse-dot h-1.5 w-1.5 rounded-full bg-ok" /> {t('common:status.operational')}</>
          ) : service === 'down' ? (
            <><span className="h-1.5 w-1.5 rounded-full bg-critical" /> {t('common:status.unreachable')}</>
          ) : (
            <><span className="h-1.5 w-1.5 rounded-full bg-muted" /> {t('common:status.checking')}</>
          )}
        </span>
        <span><span dir="ltr">© {new Date().getFullYear()}</span> {t('common:appName')}</span>
        <span className="flex items-center gap-3">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-fg">{t('support')}</a>
          {/* Version is always LTR — RTL would render it as "0.1v" / reversed. */}
          <span className="tk-num" dir="ltr">v{APP_VERSION}</span>
        </span>
      </footer>
    </div>
  );
}
